import "./TextAreaField.scss";

/**
 * Controlled labeled multiline text input with an inside-the-box label layout
 * for Plan Manager forms. Pure presentational — emits intent through callbacks;
 * owns no state.
 *
 * @param {object} props
 * @param {string} props.id - Input id; links label via htmlFor.
 * @param {string} props.label - Visible label text displayed inside the box.
 * @param {string} props.value - Controlled input value.
 * @param {(value: string) => void} props.onChange - Emits the new string value.
 * @param {() => void} [props.onBlur] - Emits blur event.
 * @param {string} [props.placeholder] - Textarea placeholder.
 * @param {string} [props.helperText] - Optional hint below the input.
 * @param {string} [props.error] - Optional error message with role="alert".
 * @param {boolean} [props.disabled] - Disables the input.
 * @param {number} [props.rows] - Number of visible text lines (default: 3).
 * @param {number} [props.maxLength] - Maximum character length.
 * @param {string} [props.className] - Extra class on the root element.
 * @returns {import('react').JSX.Element}
 */
const TextAreaField = ({
    id,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    helperText,
    error,
    disabled,
    rows = 3,
    maxLength,
    className,
}) => {
    const rootClassName = [
        "form-input",
        "plan-manager-text-area-field",
        error ? "plan-manager-text-area-field--error" : null,
        disabled ? "plan-manager-text-area-field--disabled" : null,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rootClassName}>
            <div className="plan-manager-text-area-field__box">
                <label
                    htmlFor={id}
                    className="plan-manager-text-area-field__label"
                >
                    {label}
                </label>
                <textarea
                    id={id}
                    className="plan-manager-text-area-field__textarea"
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={rows}
                    maxLength={maxLength}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                />
            </div>
            {helperText ? (
                <p className="plan-manager-text-area-field__helper">
                    {helperText}
                </p>
            ) : null}
            {error ? (
                <p
                    className="plan-manager-text-area-field__error"
                    role="alert"
                >
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default TextAreaField;
